import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { MapPin, Users, Navigation } from 'lucide-react';

export default function GeofencingPage() {
  const { user, axiosAuth } = useAuth();
  const [locations, setLocations] = useState([]);
  const [myLocation, setMyLocation] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [watchId, setWatchId] = useState(null);
  const api = axiosAuth();
  const isPeerSpecialist = user.role === 'peer_specialist';
  const canView = ['coordinator', 'admin'].includes(user.role);

  useEffect(() => {
    if (canView) {
      api.get('/location/specialists').then(r => setLocations(r.data)).catch(() => {});
      const interval = setInterval(() => {
        api.get('/location/specialists').then(r => setLocations(r.data)).catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, []);

  const startTracking = () => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
        setMyLocation(loc);
        api.post('/location/update', loc).catch(() => {});
      },
      (err) => console.error('Geolocation error:', err),
      { enableHighAccuracy: true, maximumAge: 30000 }
    );
    setWatchId(id);
    setTracking(true);
  };

  const stopTracking = () => {
    if (watchId) navigator.geolocation.clearWatch(watchId);
    setTracking(false);
    setWatchId(null);
  };

  return (
    <div className="space-y-6" data-testid="geofencing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Location Tracking</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {isPeerSpecialist ? 'Share your location for safety and coordination' : 'Monitor peer specialist locations'}
          </p>
        </div>
        {isPeerSpecialist && (
          <Button
            onClick={tracking ? stopTracking : startTracking}
            className={`rounded-full gap-2 ${tracking ? 'bg-red-500 hover:bg-red-600' : 'bg-teal-600 hover:bg-teal-700'} text-white`}
            data-testid="toggle-tracking"
          >
            <Navigation size={16} /> {tracking ? 'Stop Sharing' : 'Share Location'}
          </Button>
        )}
      </div>

      {isPeerSpecialist && myLocation && (
        <Card className="border-0 shadow-sm rounded-2xl bg-teal-50/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
              <MapPin className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-teal-800">Your Location</p>
              <p className="text-xs text-teal-600">{myLocation.latitude.toFixed(4)}, {myLocation.longitude.toFixed(4)}</p>
            </div>
            <Badge className={`ml-auto ${tracking ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}>
              {tracking ? 'Sharing' : 'Stopped'}
            </Badge>
          </CardContent>
        </Card>
      )}

      {canView && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4">
              <p className="text-2xl font-bold">{locations.length}</p>
              <p className="text-xs text-muted-foreground">Specialists Online</p>
            </CardContent></Card>
            <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-4">
              <p className="text-2xl font-bold">{locations.filter(l => {
                const updated = new Date(l.updated_at);
                return (Date.now() - updated) < 600000;
              }).length}</p>
              <p className="text-xs text-muted-foreground">Active (10 min)</p>
            </CardContent></Card>
          </div>

          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Peer Specialist Locations</CardTitle>
            </CardHeader>
            <CardContent>
              {locations.length > 0 ? (
                <div className="space-y-2">
                  {locations.map(l => {
                    const lastUpdate = new Date(l.updated_at);
                    const minutesAgo = Math.round((Date.now() - lastUpdate) / 60000);
                    const isRecent = minutesAgo < 10;
                    return (
                      <div key={l.user_id} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
                        <div className={`w-3 h-3 rounded-full ${isRecent ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{l.full_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {l.latitude.toFixed(4)}, {l.longitude.toFixed(4)} — {minutesAgo < 1 ? 'just now' : `${minutesAgo}m ago`}
                          </p>
                        </div>
                        <Badge className={isRecent ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-600'}>
                          {isRecent ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No specialist locations available yet.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
